import DocumentTitle from 'react-document-title';
import DOMPurify from 'dompurify';
import { injectIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Prompt } from 'react-router-dom';
import {
  Box,
  Spinner,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { CentralizedDocumentForm } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { useQuery } from 'react-query';
import { useContext, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { AppContext } from '../App/context';
import UserService from '../../services/userService';
import useHasPermission from '../../hooks/useHasPermission';
import { get, post, apiURL } from '../../utils/api';
import { toastr } from 'react-redux-toastr';
import { useState } from 'react';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import { buildRecertNoticeDetails, handlePreview } from '../AddDocument/utils';
import { CentralizedDocumentFormContainer } from '../AddDocument/';
import useExitPrompt from '../../hooks/useExitPrompt';
import {
  useDocumentCategories,
  useRecertNoticePrograms,
} from '../AddDocument/hooks';
import { getCookie } from '../../utils/cookie';

function EditDocument({ intl, history, match }) {
  const {
    documentManagementMvp,
    documentManagementLetters,
    documentManagementApplicationTermsAndConditions,
    recertNotices,
  } = useFlags();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const authToken = getCookie('session_id');
  const { userOrganizationId, isLdUserContextReady } = useContext(AppContext);
  const isPageEnabled = documentManagementMvp && isLdUserContextReady;
  const mergeFieldTableName = useUniqueTableName(
    'edit-document-merge-field-inventory',
  );
  const documentId = match.params.documentId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasEditPermission = useHasPermission('centralized-docs-edit');
  const { data: properties, isLoading: propertiesLoading } = useQuery(
    ['properties', userOrganizationId],
    () => {
      const service = new UserService();
      return service.getAllProperties(userOrganizationId);
    },
    {
      refetchOnWindowFocus: false,
      enabled: isPageEnabled,
    },
  );
  const documentCategories = useDocumentCategories({
    userOrganizationId,
    isPageEnabled,
    documentManagementLetters,
    documentManagementApplicationTermsAndConditions,
  });
  const recertNoticePrograms = useRecertNoticePrograms({
    userOrganizationId,
    isEnabled: isPageEnabled && recertNotices,
  });
  const { data: document, isLoading: isDocumentLoading } = useQuery(
    ['document', userOrganizationId, documentId],
    () => {
      return get(`/${userOrganizationId}/centralized-documents/${documentId}`);
    },
    {
      refetchOnWindowFocus: false,
      enabled: isPageEnabled,
    },
  );
  const { data: mergeFields, isLoading: mergeFieldsLoading } = useQuery(
    ['mergeFields'],
    () => {
      return get('/universal-merge-field-configurations?hideFromUsers=false');
    },
    {
      refetchOnWindowFocus: false,
      enabled: isPageEnabled,
    },
  );
  const initialValues = useMemo(() => {
    let householdBatch;
    let unitTypes;

    if (document?.documentType) {
      const { documentType } = document;

      unitTypes = [];
      householdBatch = [];

      if (documentType.uploadConventional) unitTypes.push('Conventional');
      if (documentType.uploadAffordable) unitTypes.push('Affordable');
      if (documentType.uploadCommercial) unitTypes.push('Commercial');

      if (documentType.isBatchTemplate) householdBatch.push('batch');
      if (documentType.isHouseholdTemplate) householdBatch.push('household');
    } else if (document?.leaseAddendum) {
      const { leaseAddendum } = document;
      unitTypes = leaseAddendum.unitTypes;
    }

    return {
      documentName: document?.name,
      documentCategoryId: document?.documentCategoryId,
      documentContent: document?.documentContent,
      footerContent: document?.footerContent,
      assignedProperties: document?.propertyIds,
      isAddendumRequired: document?.leaseAddendum?.isRequired,
      unitTypes,
      householdBatch,
      ...(recertNotices &&
        document?.documentType && {
          isRecertNotice: document.documentType.isRecertNotice,
          recertNoticeProgramIds: (
            document.documentType.recertNoticePrograms ?? []
          ).map((program) => program.masterAffordableProgramId),
          recertNoticeTiming:
            document.documentType.recertNoticeTiming ?? undefined,
        }),
    };
  }, [document, recertNotices]);

  useExitPrompt({
    active: isFormDirty && !submitted,
  });

  if (isDocumentLoading || !document) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <DocumentTitle title="Edit Document">
      <CentralizedDocumentFormContainer>
        <Prompt
          when={isFormDirty && !submitted}
          message="Any unsaved changes will be lost."
        />
        <CentralizedDocumentForm
          onFormDirtyChange={(isDirty) => {
            setIsFormDirty(isDirty);
          }}
          intl={intl}
          initialValues={initialValues}
          properties={properties ?? []}
          propertiesLoading={propertiesLoading}
          documentCategories={documentCategories.map((c) => ({
            value: c.id,
            text: c.name,
          }))}
          recertNoticePrograms={recertNoticePrograms}
          onCancel={() => history.push('/manage-documents')}
          onSubmit={async (values) => {
            try {
              setIsSubmitting(true);

              await post(
                `/${userOrganizationId}/centralized-documents/${documentId}`,
                JSON.stringify({
                  documentContent: DOMPurify.sanitize(values.documentContent),
                  footerContent: DOMPurify.sanitize(values.footerContent),
                  propertyIds: values.assignedProperties,
                  categoryDetails: {
                    isAddendumRequired: values.isAddendumRequired,
                    unitTypes: values.unitTypes ?? [],
                    householdBatch: values.householdBatch,
                    applicationType: values.applicationType,
                    recertNotice: buildRecertNoticeDetails(values),
                  },
                }),
              );
              toastr.success('Success', 'The document has been updated.');
              flushSync(() => setSubmitted(true));
              history.push('/manage-documents');
            } catch {
              toastr.error(
                'Error',
                'The document changes were unsuccessful. Please try again.',
              );
              setIsSubmitting(false);
            }
          }}
          isSubmitting={isSubmitting}
          disabled={hasEditPermission !== true}
          MergeFieldInventoryProps={{
            intl,
            mergeFields,
            isLoading: mergeFieldsLoading,
            mergeFieldTableName,
          }}
          onPreview={({ documentContent, footerContent }) =>
            handlePreview({
              documentContent,
              footerContent,
              organizationId: userOrganizationId,
            })
          }
          ifwUrl={`${apiURL}/ckeditor/import-from-word`}
          authToken={authToken}
        />
      </CentralizedDocumentFormContainer>
    </DocumentTitle>
  );
}

export default injectIntl(EditDocument);
